import { safeGetItem, safeSetItem } from "Utils/safeLocalStorage";

// Long enough that the footer is served from cache across a visit, short
// enough that a promised time is still bookable when the visitor gets there.
const TTL_MS = 30 * 60 * 1000;

export interface TAvailability {
  city: string;
  time: string;
}

interface TCached extends TAvailability {
  savedAt: number;
}

// Keyed by role and city as well as site: each names a different set of
// advisors, and a shared key would let one write another's footer.
function cacheKey(site: string, role: string, city: string): string {
  return `advisorAvailability-${site}-${role}-${city}`;
}

// Storage outlives deployments and is the visitor's to edit, so the shape is
// checked rather than trusted.
function isCached(value: unknown): value is TCached {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const cached = value as Record<string, unknown>;

  return (
    typeof cached.savedAt === "number" &&
    typeof cached.time === "string" &&
    typeof cached.city === "string"
  );
}

export function readAvailability(
  site: string,
  role: string,
  city: string
): TAvailability | null {
  const raw = safeGetItem(cacheKey(site, role, city));

  if (!raw) {
    return null;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isCached(parsed) || Date.now() - parsed.savedAt > TTL_MS) {
    return null;
  }

  return { city: parsed.city, time: parsed.time };
}

export function writeAvailability(
  site: string,
  role: string,
  city: string,
  availability: TAvailability
): void {
  safeSetItem(
    cacheKey(site, role, city),
    JSON.stringify({ ...availability, savedAt: Date.now() })
  );
}
